import $eventBus from '@/common/providers/eventbus';
import { FAIRWAY_TREATMENT, buildFairwayRedirectUrl } from '../fairwayMortgage.js';
import { preload } from '../nojq/_load.js';
import { parseQueryString } from '../nojq/_utilities.js';
import { joinLightspeedUrl, LIGHTSPEED_PATHS } from '@/common/utilities/lightspeed-url.js';
import {
    buildNonHodLightspeedPrefillFields,
    persistLightspeedHandoff,
} from '@/homeowner/utilities/lightspeed-prefill.js';

const isSSR = typeof window === 'undefined';
const context = (!isSSR && window.__INITIAL_STATE__) || {};

function getLightspeedOrigin() {
    const state = (!isSSR && window.__INITIAL_STATE__) || context || {};
    return state.lightspeedUrl || state.glb?.lightspeedUrl;
}

function getPrefillFinancialProfile() {
    const state = (!isSSR && window.__INITIAL_STATE__) || context || {};
    return state.mymovoto?.financialProfile || state.financialProfile || {};
}

function loanTypeFromLightspeedUrl(url) {
    const match = String(url || '').match(/[?&]loan_type=([^&]*)/);
    if (!match) {
        return undefined;
    }
    try {
        return decodeURIComponent(match[1]);
    } catch (e) {
        return match[1];
    }
}

/** Merge CTA sendOpt with DPP pageData geo so address/city/state are available for prefill. */
export function listingContextFromSendOpt(sendOpt = {}) {
    const state = (!isSSR && window.__INITIAL_STATE__) || context || {};
    const pageData = state.pageData || context.pageData || {};
    const pageGeo = pageData.geo || {};
    return {
        address: sendOpt.address || pageGeo.address || '',
        city: sendOpt.city || pageGeo.city || '',
        state: sendOpt.state || pageGeo.state || '',
        zipcode: sendOpt.zipcode || pageGeo.zipcode || '',
        propertyType: sendOpt.propertyType || pageData.propertyType || '',
        price: sendOpt.price || pageData.price,
    };
}

export { buildNonHodLightspeedPrefillFields };

var loadedBrankrate = 0;
const getStringWithCtx = function (template, ctx) {
    return new Function('return `' + template + '`;').call(ctx);
};

/**
 * @param {object} [sendOpt]
 * @param {'buy'|'refi'|undefined} [explicit] — override when the destination link is purchase-only or refi-only
 * @returns {'buy'|'refi'}
 */
export function mortgageListingCategory(sendOpt, explicit) {
    if (['buy', 'refi', 'heloc'].includes(explicit)) {
        return explicit;
    }
    let isRefi = /refi/i;
    if (sendOpt.id && isRefi.test(sendOpt.id)) {
        return 'refi';
    }
    if (sendOpt.url && isRefi.test(sendOpt.url)) {
        return 'refi';
    }

    return sendOpt && sendOpt.houseRealStatus === 'SOLD' ? 'refi' : 'buy';
}

/**
 * GA4: mortgage_click before redirect / partner handoff. link_name = clicked control text (cta).
 * @param {{ label: string, linkName: string, category: 'buy'|'refi' }} opts
 */
export function emitMortgageClickGa4({ label, linkName, category }) {
    if (isSSR) return;
    const name = (linkName && String(linkName).trim()) || 'unknown';
    const cat = ['refi', 'heloc'].includes(category) ? category : 'buy';
    $eventBus.$emit('ga', {
        ec: cat,
        en: 'mortgage_click',
        el: label,
        props: {
            link_name: name,
        },
    });
}

/** Lower partner Lightspeed URL. Prefill requires POST — query params match existing Movoto→Lower CTAs. */
export function utmContentFromCtaLabel(label) {
    const slug = String(label || '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_|_$/g, '');
    return slug ? `homeowner_${slug}` : 'homeowner';
}

export function buildLowerPartnerUrl({ lightspeedUrl, loanType, utmContent, pathname, property = {}, reaQuestion = true, pageType }) {
    const lowerUrl = joinLightspeedUrl(lightspeedUrl, LIGHTSPEED_PATHS.prefill);
    const geo = property.geo || {};
    const redirectPath = pathname || (typeof location !== 'undefined' ? location.pathname : '/');
    let redirectUrl =
        `${lowerUrl}?partner_name=movoto` +
        `&rea_question=${reaQuestion ? 'true' : 'false'}` +
        `&consent_required=true&show_warm_up=false` +
        `&redirect_url=${encodeURIComponent('https://www.movoto.com' + redirectPath)}`;

    // Omit loan_type when unset so Lightspeed asks the borrower to pick (e.g. HELOC vs cash-out).
    if (loanType) {
        redirectUrl += `&loan_type=${encodeURIComponent(loanType)}`;
    }
    if (utmContent) {
        redirectUrl += `&utm_content=${utmContent}`;
    }

    const zip = geo.zipcode || geo.zipCode || geo.zip || '';
    const state = geo.state || '';
    const city = geo.city || '';
    // Same shape as post-lead / ojolink Lower CTAs (one logical field per utm_term).
    if (state || city || zip) {
        redirectUrl += `&utm_term=state=${state}&utm_term=city=${encodeURIComponent(city)}&utm_term=zip=${zip}`;
    }
    if (property.propertyType) {
        redirectUrl += `&utm_campaign_content=property_type=${property.propertyType}`;
    }
    if (pageType) {
        redirectUrl += `&utm_campaign_content=page_type=${pageType}`;
    }

    return redirectUrl;
}

export function startSessionEvent(ga) {
    if (ga.ec) {
        localStorage.setItem('ec', ga.ec);
        if (ga.en) {
            $eventBus.$emit('ga', ga);
        }
    }
}

export function endSessionEvent(ga) {
    if (ga.ec) {
        localStorage.setItem('ec', '');
        if (ga.en) {
            $eventBus.$emit('ga', ga);
        }
    }
}
/**
 * Open interstitial (or other tracked link). When redirectUrl is set, stash Lightspeed
 * prefill URL + POST fields for confirm-mortgage-lower to submit after Confirm.
 * @param {object} [prefillFields] — optional explicit POST body
 * @param {object} [listing] — DPP/listing context (address/city/state/zip/price/propertyType)
 */
export function openLinkWithTrack(linkOptions, ga, redirectUrl, mortgageClick, prefillFields, listing) {
    if (mortgageClick) {
        emitMortgageClickGa4(mortgageClick);
    }
    if (redirectUrl) {
        const state = (!isSSR && window.__INITIAL_STATE__) || context || {};
        const user = state.user || state.glb?.user || context.user || {};
        const fields =
            prefillFields !== undefined
                ? prefillFields
                : buildNonHodLightspeedPrefillFields({
                      user,
                      loanType: loanTypeFromLightspeedUrl(redirectUrl),
                      financialProfile: getPrefillFinancialProfile(),
                      listing: listingContextFromSendOpt(listing || {}),
                  });
        persistLightspeedHandoff({ url: redirectUrl, fields });
    }
    if (!ga) {
        console.error(`notrack:${linkOptions.url}`);
    } else {
        startSessionEvent(ga);
    }
    //make sure the event be send before open new tab
    setTimeout(() => {
        window.open(linkOptions.url, linkOptions.target || '_blank');
    }, 100);
};
export const openMRCPopup = function (item, cta, componentTrigger, sendOpt) {
    emitMortgageClickGa4({
        label: 'MRC',
        linkName: cta,
        category: mortgageListingCategory(sendOpt),
    });
    $eventBus.$emit('dialog-center', {
        name: 'mrcMortgageRates',
        trigger: componentTrigger,
        opt: {
            showNormalMrc: true,
            utm_campaign: item?.utm_campaign,
            desc: item?.desc,
            cta: cta,
            ga: 'partner_mortgage_listings_session',
        },
    });
};
export const openMortgagePopUp = function (trigger, pid, cta, componentTrigger, mortgageClick) {
    if (mortgageClick) {
        emitMortgageClickGa4({
            label: mortgageClick.label,
            linkName: mortgageClick.linkName,
            category: 'refi',
        });
    }
    $eventBus.$emit('dialog-center', {
        name: '3wayPayment',
        trigger: componentTrigger,
        opt: {
            showRefinance: true,
            pid: pid,
            triggerId: trigger,
            cta: cta,
        },
    });
};
const getSplit = function (split, treatment) {
    return context.splits[split] === treatment;
};

export const loadInsurancePop = (zipcode, triggerId = '') => {
    var url = `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_01/textLink/insurance?zipcode=${zipcode}`;
    console.log(url);
    if (triggerId) {
        $eventBus.$emit('ga', { ec: 'partner', en: 'insurance_session', el: 'cordless', props: { link_name: triggerId } });
    }
    // emitMortgageClickGa4({
    //     label: 'Cordless',
    //     linkName: (triggerId && String(triggerId).trim()) || 'insurance',
    //     category: 'buy',
    // });
    window.open(url, '_blank');
};

export const loadBankratePop = () => {
    return new Promise((resolve) => {
        //has load
        if (loadedBrankrate === 2) {
            resolve(true);
        } else if (loadedBrankrate === 0) {
            loadedBrankrate = 1;
            preload({ files: ['https://elf.bankrate.com/embedded.umd.min.js'], type: 'js' }).then(function () {
                loadedBrankrate = 2;
                resolve(true);
            });
        }
    });
};

const CM_ADS_BASE_URL = 'https://ads.cordlessmedia.com/ad-wrapper/89984/cm.min.js';

// Maps raw Split.io treatment to the partner-facing cmWrapper value (lowercase, no underscore).
// Unknown or missing values fall back to 'control' (safe default).
export const getCmWrapperTreatment = (splitValue) => {
    if (splitValue === 'serversideprebid' || splitValue === 'hybridprebid') return splitValue;
    return 'control';
};

// Maps raw Split.io treatment to the DebugBear analytics label.
// Unknown or missing values fall back to 'CONTROL' (safe default).
export const getCmAdsTreatmentLabel = (splitValue) => {
    if (splitValue === 'serversideprebid' || splitValue === 'hybridprebid') return splitValue.toUpperCase();
    return 'CONTROL';
};

// First call wins — subsequent calls ignore treatmentSplitValue because the
// script singleton (window.adsLoaded) is already in-flight or resolved.
export const loadCmAds = (treatmentSplitValue) => {
    return new Promise((resolve) => {
        if (window.adsLoaded === 2) {
            resolve(window.cmWrapper);
        } else {
            $(document).one('adsLoaded', function (e, cmWrapper) {
                resolve(cmWrapper);
            });
            if (!window.adsLoaded) {
                window.adsLoaded = 1;
                preload({ files: [CM_ADS_BASE_URL], type: 'js' }).then(function () {
                    if (!window.cmWrapper) {
                        window.cmWrapper = {};
                    }
                    if (!window.cmWrapper.que) {
                        window.cmWrapper.que = [];
                    }
                    // Set partner treatment before consumers run via the adsLoaded event.
                    window.cmWrapper.treatment = getCmWrapperTreatment(treatmentSplitValue);
                    window.adsLoaded = 2;
                    $(document).trigger('adsLoaded', window.cmWrapper);
                });
            }
        }
    });
};

export const loadDefaultMRCPopUp = (trigger, cta, sendOpt) => {
    let fallback = '';
    if (trigger === 'apply') {
        fallback = 'getpreapprovedATF_LP';
    } else if (trigger === 'getpreapproved') {
        fallback = 'getpreapproved_LP';
    } else if (trigger === 'findalender') {
        fallback = 'findlender_LP';
    }
    if (fallback) {
        fallback += '_NEW';
    }
    return openLinkWithTrack(
        { url: `${window.__INITIAL_STATE__.appUrl}mortgage/mrc/?title=getpreapproved&fallback=${fallback}` },
        { ec: 'partner_mortgage_listings_session', en: 'long_form', el: 'MRC', props: { link_name: cta } },
        undefined,
        { label: 'MRC', linkName: cta, category: mortgageListingCategory(sendOpt) }
    );
};

export const loadMortgagePopUp = (trigger, pid, cta, mortgageClick) => {
    if (mortgageClick) {
        emitMortgageClickGa4({
            label: mortgageClick.label,
            linkName: mortgageClick.linkName,
            category: 'refi',
        });
    }
    $eventBus.$emit('dialog-center', {
        name: '3wayPayment',
        trigger: 'loadOjolink',
        opt: {
            showRefinance: true,
            pid: pid,
            triggerId: trigger,
            cta: cta,
        },
    });
};

/**
 * Build the `list_price` / `sold_price` UTM tail for Lower Lightspeed on sold or
 * off-market DPPs. sendOpt.price on those pages is the sold/close price (or a stale
 * valuation), so we prefer the AVM valuation for `list_price` and the close price for
 * `sold_price`. For off-market listings without a close price, mirror the AVM into
 * `sold_price` so downstream partners can key on either value.
 */
function getSoldPriceUtm() {
    let url = '';
    const pd = context.pageData || {};
    const avm = pd.avmInfo && pd.avmInfo.valuation;
    const close = pd.closePrice;
    if (avm) {
        url += `&utm_term=list_price=${avm}`;
    }
    if (close) {
        url += `&utm_term=sold_price=${close}`;
    } else if (avm) {
        url += `&utm_term=sold_price=${avm}`;
    }
    return url;
}

/**
 * Movoto uses 'ALL' as a catch-all property type on off-market / property-record DPPs;
 * Lower Lightspeed expects 'OTHER' for unknown types.
 */
function mapLightspeedPropertyType(type) {
    return type === 'ALL' ? 'OTHER' : type || '';
}

export const openBankratePop = (inputOpt) => {
    if (!inputOpt) {
        return;
    }
    var sendOpt = {
        price: 300000,
        pid: 'movoto_branded',
        triggerId: 'getquotes',
        utm_source: 'ojo_movoto',
        customization: 'movotoShort',
        zipcode: '',
        address: '',
        city: '',
        state: '',
        email: '',
        phone: '',
        firstName: '',
        lastName: '',
    };

    var winOpt = {};
    if (window && window.location && window.location.search) {
        winOpt = parseQueryString(window.location.search);
    }
    Object.assign(sendOpt, winOpt);
    Object.assign(sendOpt, inputOpt);
    const trackLink = (linkOptions, ga, redirectUrl, mortgageClick, prefillFields) =>
        openLinkWithTrack(linkOptions, ga, redirectUrl, mortgageClick, prefillFields, sendOpt);
    const linkLabel = String(sendOpt.cta ?? '').trim();
    const linkName = (fallback) => {
        if (linkLabel) return linkLabel;
        if (fallback == null) return '';
        return String(fallback).trim();
    };
    const mc = (label, explicitCat, linkNameFallback) => ({
        label,
        linkName: linkName(linkNameFallback),
        category: mortgageListingCategory(sendOpt, explicitCat),
    });
    //overwrite logic from dialogcenter ---start
    if (sendOpt.propertyType === 'MOBILE') {
        const mobileMRCTreatment = [
            {
                utm_campaign: 'apply_mobile',
                trigger: 'apply',
            },
            {
                utm_campaign: 'getprequalified_mobile',
                trigger: 'calcfindalender',
            },
            {
                utm_campaign: 'prequalify_mobile',
                trigger: 'prequalify',
            },
            {
                utm_campaign: 'findlender_mobile',
                trigger: 'findalender',
            },
            {
                utm_campaign: 'getpreapproved_mobile',
                trigger: 'getpreapproved',
            },
        ];
        let MRCSplitForMobileProperty = mobileMRCTreatment.find((item) => {
            if (sendOpt.triggerId === item.trigger) {
                return true;
            }
        });
        openMRCPopup(MRCSplitForMobileProperty, sendOpt.cta, sendOpt.componentTrigger, sendOpt);
        return;
    }

    const splitsTreatment = [
        {
            trigger: 'compareMortRates',
            pid: 'movoto_listingsapi',
        },
        {
            split: 'movoto-cpc-preapproved-cw-9240',
            treatment: 'on4',
            trigger: 'calcfindalender',
            pid: 'movoto_listingsapi',
        },
    ];
    let splitItem = splitsTreatment.find((item) => {
        if (
            (sendOpt.triggerId === item.trigger && (getSplit(item.split, item.treatment) || getSplit(item.split, 'default') || getSplit(item.split, 'off'))) ||
            sendOpt.triggerId === 'compareMortRates'
        ) {
            return true;
        }
    });
    if (splitItem) {
        openMortgagePopUp(splitItem.trigger, splitItem.pid, sendOpt.cta, sendOpt.componentTrigger, mc('Bankrate', 'buy'));
        return;
    }
    //overwrite logic from dialogcenter ---end
    if (sendOpt.propertyType) {
        if (sendOpt.propertyType === 'MOBILE') {
            sendOpt.pid = 'movoto_mobile';
        } else if (sendOpt.propertyType === 'LAND') {
            sendOpt.pid = 'movoto_land';
        }
    }
    if (sendOpt.fullName && !sendOpt.firstName && !sendOpt.lastName) {
        const names = sendOpt.fullName.split(' ');
        sendOpt.firstName = names[0];
        sendOpt.lastName = names[1];
    }
    if (sendOpt.url === 'credit' || sendOpt.url === 'rentalCredit') {
        let linkName = 'checkhereshortform',
            url = '',
            el = '';
        if (sendOpt.url == 'credit') {
            if (sendOpt.houseRealStatus === 'ACTIVE' || sendOpt.houseRealStatus === 'SOLD' || sendOpt.houseRealStatus === 'OFF_MARKET') {
                if (sendOpt.triggerId === 'checkInterest' || (sendOpt.triggerId == 'checkYourCredit' && sendOpt.trigger == 'propertyMortgage')) {
                    linkName = `${sendOpt.propertyType}_calculatorsection`;
                    el = 'cordless';
                    url = `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_07/textLink/credit`;
                } else if (sendOpt.triggerId === 'creditCheck') {
                    linkName = `${sendOpt.propertyType}__thankyoupage`;
                } else if (sendOpt.triggerId === 'homebuyers_checkYourCredit') {
                    linkName = `${sendOpt.propertyType}_hoafee`;
                    el = 'cordless';
                    url = `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_07/textLink/credit`;
                }
            }
            if (sendOpt.triggerId.indexOf('creditCheckRent') > -1) {
                linkName = sendOpt.triggerId.indexOf('MultiMsg') > -1 ? 'rental_thankyoupage_mm' : 'rental_thankyoupage';
                el = 'cordless';
                url = `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_07/textLink/credit`;
            }
        } else if (sendOpt.url === 'rentalCredit') {
            linkName = 'rental_abovethefold';
            url = 'https://links.cordlessmedia.com/staticlink/89984/89984_textLink_07/textLink/credit';
        }

        if (linkName) {
            $eventBus.$emit('ga', { ec: 'partner', en: 'credit_monitoring_session', el: el ? el : 'cordless', props: { link_name: `${linkName}` } });
        }
        // emitMortgageClickGa4({
        //     label: 'Cordless',
        //     linkName: linkLabel || linkName,
        //     category: mortgageListingCategory(sendOpt),
        // });
        window.open(url ? url : 'https://links.cordlessmedia.com/staticlink/89984/89984_textLink_07/textLink/credit', '_blank');
        return;
    }
    var isMinorProperty = sendOpt.price < 100000 || ['LAND', 'MOBILE'].includes(sendOpt.propertyType);

    var url = sendOpt.url;
    var elDict = {
        preapprove: 'bankrate',
        preapproveapply: 'bankrate',
        preapproveCalc: 'bankrate',
        prequalify: 'bankrate',
        veteranseligibility: 'Bankrate_Veterans_topcalc',
        veteranseligibility1: 'Veterans United Top Calc',
        veteranseligibility1NewDpp: 'Veterans United Top Calc (New DPP)',
        veteranseligibility2: 'Veterans United Top Calc',
        veteranseligibility3: 'VeteransUnited_topcalc2',
        veteranscanafford: 'Veterans United Below Calc',
        veteranscanafford1: 'VeteransUnited_TYP',
        veteranscanafford2: 'VeteransUnited_TYP_V2',
        veteranscanafford3: 'Veterans United Below Calc',
        veteranscanafford4: 'Bankrate_Veterans_bottomcalc',
        veteranscanafford6: 'Veterans United Below Calc',
        veteranscanafford6NewDpp: 'Veterans United Below Calc (New DPP)',
        veteranslink: 'Bankrate_Veterans_TYP',
    };
    var category;
    var props = {};
    let lowerUrl = joinLightspeedUrl(getLightspeedOrigin(), LIGHTSPEED_PATHS.prefill);
    if (sendOpt.cta) {
        props = {
            link_name: sendOpt.cta,
        };
    }
    if (url === 'mortgagepages') {
        // emitMortgageClickGa4({
        //     label: 'Cordless',
        //     linkName: linkName('mortgagepages'),
        //     category: mortgageListingCategory(sendOpt),
        // });
        window.open(`https://links.cordlessmedia.com/staticlink/89984/89984_textLink_18/PreApprovalNavPageApply/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}`, '_blank');
        $eventBus.$emit('ga', { ec: 'partner_mortgage_page_session', en: 'lender_site', el: 'Cordless', props: { link_name: 'mortgagepages' } });
        return;
    }
    if (url === 'veteranshub1') {
        emitMortgageClickGa4({
            label: 'Veteran',
            linkName: linkName('Prequalify for a 0% Down VA Loan'),
            category: mortgageListingCategory(sendOpt, 'buy'),
        });
        window.open(
            'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.361482107;dc_trk_aid=552469127;dc_trk_cid=188175518;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=',
            '_blank'
        );
        $eventBus.$emit('ga', { ec: 'partner', en: 'mortgage_session', el: 'VeteransUnited_vethub1', props: { link_name: 'Prequalify for a 0% Down VA Loan' } });
        return;
    }
    if (url === 'veteranshub2') {
        emitMortgageClickGa4({
            label: 'Veteran',
            linkName: linkName('Get Pre-Qualified for a VA Loan'),
            category: mortgageListingCategory(sendOpt, 'buy'),
        });
        window.open(
            'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.361665157;dc_trk_aid=552630610;dc_trk_cid=188178971;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=',
            '_blank'
        );
        $eventBus.$emit('ga', { ec: 'partner', en: 'mortgage_session', el: 'VeteransUnited_vethub2', props: { link_name: 'Get Pre-Qualified for a VA Loan' } });
        return;
    }
    if (url === 'veteransdrawer') {
        if (context.splits['movoto-military-mortgage-CW-3875'] === 'on14') {
            let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=veterans_prequal&vendor_lead_unique_id=${
                context.user?.id
            }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
            return trackLink(
                {
                    url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=veteran',
                },
                { ec: 'partner_mortgage_purchase_veteran_listings', en: 'partner_mortgage_click_prequalify', el: 'Lower', props: { link_name: sendOpt.cta } },
                redirectUrl,
                mc('Lower', 'buy')
            );
        }
        return trackLink(
            {
                url: 'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.361855638;dc_trk_aid=552469235;dc_trk_cid=187987699;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=',
            },
            { ec: 'long_form', en: 'partner_mortgage_listing_session', el: 'Veterans United Drop Drawer', props: { link_name: 'Prequalify for a 0% Down VA Loan' } },
            undefined,
            {
                label: 'Veteran',
                linkName: linkName('Prequalify for a 0% Down VA Loan'),
                category: mortgageListingCategory(sendOpt, 'buy'),
            }
        );
    }
    if (url === 'typautopop') {
        emitMortgageClickGa4({
            label: 'Landis',
            linkName: linkName('Rent-to-own options_postreSF'),
            category: mortgageListingCategory(sendOpt),
        });
        window.open('https://apply.landis.com/movoto/?utm_source=movoto&utm_medium=paid_partner&utm_campaign=rental_listing&utm_content=postreSF', '_blank');
        $eventBus.$emit('ga', { ec: 'partner', en: 'alternative_financing_session', el: 'Landis', props: { link_name: 'Rent-to-own options_postreSF' } });
        return;
    }
    //checked still be used for /rent-to-own
    if (url == 'renttoown') {
        //cw-9699
        if (sendOpt.triggerId === 'renttoown1') {
            emitMortgageClickGa4({
                label: 'Landis',
                linkName: linkName(`${sendOpt.cta || ''}- LP`),
                category: mortgageListingCategory(sendOpt),
            });
            window.open('https://apply.landis.com/movoto/?utm_source=movoto&utm_medium=paid_partner&utm_campaign=landing_page&utm_content=RTOLP', '_blank');
            $eventBus.$emit('ga', { ec: 'partner', en: 'alternative_financing_session', el: 'Landis', props: { link_name: `${sendOpt.cta}- LP` } });
            return;
        } else if (sendOpt.triggerId === 'renttoown2') {
            emitMortgageClickGa4({
                label: 'Divvy',
                linkName: linkName(`${sendOpt.cta || ''}- LP`),
                category: mortgageListingCategory(sendOpt),
            });
            window.open('https://www.divvyhomes.com/partners/movoto?ref=affiliates&utm_source=movoto&utm_medium=partners&utm_content=RTOLP', '_blank');
            $eventBus.$emit('ga', { ec: 'partner', en: 'alternative_financing_session', el: 'Divvy', props: { link_name: `${sendOpt.cta}- LP` } });
            return;
        }
        //cw-10444
        else if (sendOpt.triggerId === 'renttoown4') {
            emitMortgageClickGa4({
                label: 'Landis',
                linkName: linkName('Rent-to-own options_listingsSF'),
                category: mortgageListingCategory(sendOpt),
            });
            window.open('https://apply.landis.com/movoto/?utm_source=movoto&utm_medium=paid_partner&utm_campaign=active_listing&utm_content=listingsSF', '_blank');
            $eventBus.$emit('ga', { ec: 'partner', en: 'alternative_financing_session', el: 'Landis', props: { link_name: 'Rent-to-own options_listingsSF' } });
            return;
        }
    }
    if (url === 'cordlessTYP') {
        // emitMortgageClickGa4({
        //     label: 'Cordless',
        //     linkName: linkName(),
        //     category: mortgageListingCategory(sendOpt),
        // });
        window.open(`https://links.cordlessmedia.com/staticlink/89984/89984_textLink_19/Sure/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}&price=${sendOpt.price}`, '_blank');
        $eventBus.$emit('ga', { ec: 'mortage_partner_TYP', en: 'cpc ', el: 'Cordless Post RE Click', props: { link_name: sendOpt.cta } });
        return;
    }

    if (sendOpt.triggerId === 'lower_veteran_top') {
        let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=veterans_check&vendor_lead_unique_id=${
            context.user?.id
        }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
        return trackLink(
            {
                url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=veteran',
            },
            { ec: 'partner_mortgage_purchase_veteran_listings', en: 'partner_mortgage_click_check', el: 'Lower', props: { link_name: sendOpt.cta } },
            redirectUrl,
            mc('Lower', 'buy')
        );
    } else if (sendOpt.triggerId === 'lower_veteran_bottom') {
        let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=veterans_see&vendor_lead_unique_id=${
            context.user?.id
        }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
        return trackLink(
            {
                url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=veteran',
            },
            { ec: 'partner_mortgage_purchase_veteran_listings', en: 'partner_mortgage_click_see', el: 'Lower', props: { link_name: sendOpt.cta } },
            redirectUrl,
            mc('Lower', 'buy')
        );
    }
    if (!isMinorProperty && sendOpt.triggerId === 'apply') {
        switch (context.splits['movoto-cpc-apply-cw-9238']) {
            case 'on2': {
                const emailSha256 = context && context.user && context.user.sha256 ? `&cme=${context.user.sha256}` : '';
                return trackLink(
                    {
                        url: `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_09/ApplyNowButton/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}&city=${sendOpt.city}&address=${sendOpt.address}&price=${sendOpt.price}${emailSha256}`,
                    },
                    { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: 'Cordless', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('Cordless', 'buy')
                );
            }
            case 'on3': {
                return trackLink(
                    { url: window.__INITIAL_STATE__.appUrl + 'mortgage/mrc/?title=getpreapproved&fallback=getpreapprovedATF_LP_NEW' },
                    { ec: 'partner_mortgage_listings_session', en: 'long_form', el: 'MRC', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('MRC', 'buy')
                );
            }
            case 'on8': {
                return trackLink(
                    {
                        url: `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_22/NewApplyNowButton/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}&price=${sendOpt.price}`,
                    },
                    { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: 'Cordless', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('Cordless', 'buy')
                );
            }
            case 'on14':
            case 'on16': {
                const pqTest = context.splits['movoto-cpc-apply-cw-9238'] === 'on16' ? '_pq_test' : '';
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=get_preapproved_top${pqTest}&vendor_lead_unique_id=${
                    context.user?.id
                }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/',
                    },
                    { ec: 'partner_mortgage_purchase_listings', en: `partner_mortgage_click_get_preapproved_atf${pqTest}`, el: 'Lower', props: { link_name: sendOpt.cta } },
                    redirectUrl,
                    mc('Lower', 'buy')
                );
            }
            case 'on15': {
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=refinance&utm_content=lower_my_payment_top&vendor_lead_unique_id=${
                    context.user?.id
                }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}`;
                //for inactive listing we dont have avm and close price in sendopt obj
                //so create a quick fix to get them from the page context directly
                redirectUrl += getSoldPriceUtm(redirectUrl);
                redirectUrl += `&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=inactive',
                    },
                    { ec: 'partner_mortgage_refi_listings', en: 'partner_mortgage_click_lower_my_payment_atf', el: 'Lower', props: { link_name: sendOpt.cta } },
                    redirectUrl,
                    mc('Lower', 'refi')
                );
            }
            case FAIRWAY_TREATMENT['movoto-cpc-apply-cw-9238']: {
                return trackLink(
                    { url: buildFairwayRedirectUrl(sendOpt) },
                    { ec: 'partner_mortgage_purchase_listings', en: 'lender_site', el: 'Fairway', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('Fairway', 'buy')
                );
            }
        }
    } else if (!isMinorProperty && ['getpreapproved', 'calcfindalender'].indexOf(sendOpt.triggerId) > -1) {
        let cta = sendOpt.triggerId === 'calcfindalender' ? 'Get Prequalified' : 'Get Preapproved';
        switch (context.splits['movoto-cpc-preapproved-cw-9240']) {
            case 'on3': {
                let emailSha256 = context && context.user && context.user.sha256 ? `&cme=${context.user.sha256}` : '';
                return trackLink(
                    {
                        url: `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_10/GetPreApproved/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}&city=${sendOpt.city}&address=${sendOpt.address}&price=${sendOpt.price}${emailSha256}`,
                    },
                    { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: 'Cordless', props: { link_name: cta } },
                    undefined,
                    mc('Cordless', 'buy', cta)
                );
            }
            case 'on4': {
                let mrcUrl = window.__INITIAL_STATE__.appUrl + 'mortgage/mrc/?title=getpreapproved&fallback=getpreapproved_LP_NEW';
                return trackLink(
                    { url: mrcUrl },
                    { ec: 'partner_mortgage_listings_session', en: 'long_form', el: 'MRC', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('MRC', 'buy')
                );
            }
            case 'on9': {
                return trackLink(
                    { url: `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_10/GetPreApproved/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}&price=${sendOpt.price}` },
                    { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: 'Cordless', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('Cordless', 'buy')
                );
            }
            case 'on14':
            case 'on16': {
                const pqTest = context.splits['movoto-cpc-preapproved-cw-9240'] === 'on16' ? '_pq_test' : '';
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=get_preapproved_button${pqTest}&vendor_lead_unique_id=${
                        context.user?.id
                    }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/',
                    },
                    { ec: 'partner_mortgage_purchase_listings', en: `partner_mortgage_click_get_preapproved_button${pqTest}`, el: 'Lower', props: { link_name: sendOpt.cta } },
                    redirectUrl,
                    mc('Lower', 'buy')
                );
            }
            case 'on15': {
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=refinance&utm_content=lower_my_payment_button&vendor_lead_unique_id=${
                        context.user?.id
                    }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}`;
                //for inactive listing we dont have avm and close price in sendopt obj
                //so create a quick fix to get them from the page context directly
                redirectUrl += getSoldPriceUtm(redirectUrl);
                redirectUrl += `&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=inactive',
                    },
                    { ec: 'partner_mortgage_refi_listings', en: 'partner_mortgage_click_lower_my_payment_button', el: 'Lower', props: { link_name: sendOpt.cta } },
                    redirectUrl,
                    mc('Lower', 'refi')
                );
            }
            case 'on17': {
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=heloc&utm_content=get_cash_card_btn&vendor_lead_unique_id=${
                    context.user?.id
                }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}`;
                // Sold / off-market DPPs: prefer AVM valuation for list_price + close price for sold_price.
                redirectUrl += getSoldPriceUtm();
                redirectUrl += `&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${
                    sendOpt.zipcode
                }&utm_campaign_content=property_type=${mapLightspeedPropertyType(sendOpt.propertyType)}&utm_campaign_content=property_status=${
                    sendOpt.houseRealStatus
                }&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=heloc',
                    },
                    {
                        ec: 'partner_mortgage_heloc_listings',
                        en: 'partner_mortgage_click_check_eligibility_card',
                        el: 'Lower',
                        props: { link_name: sendOpt.cta },
                    },
                    redirectUrl,
                    mc('Lower', 'heloc')
                );
            }
            case FAIRWAY_TREATMENT['movoto-cpc-preapproved-cw-9240']: {
                return trackLink(
                    { url: buildFairwayRedirectUrl(sendOpt) },
                    { ec: 'partner_mortgage_purchase_listings', en: 'lender_site', el: 'Fairway', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('Fairway', 'buy')
                );
            }
            default:
                break;
        }
    } else if (!isMinorProperty && sendOpt.triggerId === 'findalender') {
        switch (context.splits['movoto-cpc-lender-cw-9242']) {
            case 'on3': {
                const emailSha256 = context && context.user && context.user.sha256 ? `&cme=${context.user.sha256}` : '';
                emitMortgageClickGa4(mc('Cordless', 'buy'));
                window.open(
                    `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_11/FindALender/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}&city=${sendOpt.city}&address=${sendOpt.address}&price=${sendOpt.price}${emailSha256}`,
                    '_blank'
                );
                $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: 'Cordless', props: { link_name: sendOpt.cta } });
                return;
            }
            case 'on4': {
                emitMortgageClickGa4(mc('MRC', 'buy'));
                window.open(window.__INITIAL_STATE__.appUrl + 'mortgage/mrc/?title=findlender&fallback=findlender_LP_NEW');
                $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'Long Form', el: 'MRC', props: { link_name: sendOpt.cta } });
                return;
            }
            case 'on9': {
                emitMortgageClickGa4(mc('Cordless', 'buy'));
                window.open(
                    `https://links.cordlessmedia.com/staticlink/89984/89984_textLink_11/FindALender/mortgage?zipcode=${sendOpt.zipcode}&state=${sendOpt.state}&price=${sendOpt.price}`,
                    '_blank'
                );
                $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: 'Cordless', props: { link_name: sendOpt.cta } });
                return;
            }
            case 'on14':
            case 'on16': {
                const pqTest = context.splits['movoto-cpc-lender-cw-9242'] === 'on16' ? '_pq_test' : '';
                let utmContent = 'find_a_lender';
                let isGetPreApproved = sendOpt.url === 'get_pre_approved';
                if (isGetPreApproved) {
                    utmContent = 'get_pre_approved';
                }
                utmContent += pqTest;
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=${utmContent}&vendor_lead_unique_id=${
                    context.user?.id
                }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
                const enBase = isGetPreApproved ? 'partner_mortgage_click_get_pre_approved' : 'partner_mortgage_click_find_a_lender';
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/',
                    },
                    {
                        ec: 'partner_mortgage_purchase_listings',
                        en: `${enBase}${pqTest}`,
                        el: 'Lower',
                        props: { link_name: sendOpt.cta },
                    },
                    redirectUrl,
                    mc('Lower', 'buy')
                );
            }
            case 'on15': {
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=refinance&utm_content=lower_my_payment_calc&vendor_lead_unique_id=${
                    context.user?.id
                }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}`;
                //for inactive listing we dont have avm and close price in sendopt obj
                //so create a quick fix to get them from the page context directly
                redirectUrl += getSoldPriceUtm(redirectUrl);
                redirectUrl += `&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=inactive',
                    },
                    { ec: 'partner_mortgage_refi_listings', en: 'partner_mortgage_click_lower_my_payment_calc', el: 'Lower', props: { link_name: sendOpt.cta } },
                    redirectUrl,
                    mc('Lower', 'refi')
                );
            }
            case FAIRWAY_TREATMENT['movoto-cpc-lender-cw-9242']: {
                return trackLink(
                    { url: buildFairwayRedirectUrl(sendOpt) },
                    { ec: 'partner_mortgage_purchase_listings', en: 'lender_site', el: 'Fairway', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('Fairway', 'buy')
                );
            }
        }
    } else if (sendOpt.triggerId === 'fha') {
        //todo merge with new utm setting in cw-3586
        switch (context.splits['movoto-calcpayment-cw-12068']) {
            case 'on14':
            case 'on16': {
                const pqTest = context.splits['movoto-calcpayment-cw-12068'] === 'on16' ? '_pq_test' : '';
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=check_fha_eligibility${pqTest}&vendor_lead_unique_id=${
                    context.user?.id
                }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=fha',
                    },
                    { ec: 'partner_mortgage_purchase_FHA_listings', en: `partner_mortgage_click_check_fha_eligibility${pqTest}`, el: 'Lower', props: { link_name: sendOpt.cta } },
                    redirectUrl,
                    mc('Lower', 'buy')
                );
            }
            case 'on17': {
                let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=heloc&utm_content=access_equity_text_cta&vendor_lead_unique_id=${
                    context.user?.id
                }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}`;
                // Sold / off-market DPPs: prefer AVM valuation for list_price + close price for sold_price.
                redirectUrl += getSoldPriceUtm();
                redirectUrl += `&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${
                    sendOpt.zipcode
                }&utm_campaign_content=property_type=${mapLightspeedPropertyType(sendOpt.propertyType)}&utm_campaign_content=property_status=${
                    sendOpt.houseRealStatus
                }&utm_campaign_content=page_type=${sendOpt.pageType}`;
                return trackLink(
                    {
                        url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=heloc',
                    },
                    {
                        ec: 'partner_mortgage_heloc_listings',
                        en: 'partner_mortgage_click_check_eligibility_text',
                        el: 'Lower',
                        props: { link_name: sendOpt.cta },
                    },
                    redirectUrl,
                    mc('Lower', 'heloc')
                );
            }
            case FAIRWAY_TREATMENT['movoto-calcpayment-cw-12068']: {
                return trackLink(
                    { url: buildFairwayRedirectUrl(sendOpt) },
                    { ec: 'partner_mortgage_purchase_listings', en: 'lender_site', el: 'Fairway', props: { link_name: sendOpt.cta } },
                    undefined,
                    mc('Fairway', 'buy')
                );
            }
            default:
                break;
        }
    } else if (sendOpt.triggerId === 'hotlead_btn') {
        let en = '',
            utm_content = '';

        if (context.splits['movoto-cta-move-CW-3672'] === 'on1') {
            en = 'partner_mortgage_click_get_preapproved_secondary';
            utm_content = 'get_preapproved_secondary';
        } else if (context.splits['movoto-cta-move-CW-3672'] === 'on2') {
            en = 'partner_mortgage_click_get_prequalified_secondary';
            utm_content = 'get_prequalified_secondary';
        }
        let redirectUrl = `${lowerUrl}?partner_name=movoto&rea_question=false&consent_required=true&show_warm_up=false&loan_type=purchase&utm_content=${utm_content}&vendor_lead_unique_id=${
            context.user?.id
        }&redirect_url=${encodeURIComponent('https://www.movoto.com' + location.pathname)}&utm_term=list_price=${sendOpt.price}&utm_term=state=${sendOpt.state}&utm_term=city=${encodeURIComponent(sendOpt.city)}&utm_term=zip=${sendOpt.zipcode}&utm_campaign_content=property_type=${sendOpt.propertyType}&utm_campaign_content=property_status=${sendOpt.houseRealStatus}&utm_campaign_content=page_type=${sendOpt.pageType}`;
        return trackLink(
            {
                url: window.__INITIAL_STATE__.appUrl + 'about/confirm-mortgage-lower/?id=fha',
            },
            { ec: 'partner_mortgage_purchase_listings', en: en, el: 'Lower', props: { link_name: sendOpt.cta } },
            redirectUrl,
            mc('Lower', 'buy')
        );
    } else if (sendOpt.triggerId === 'veteranseligibility1') {
        emitMortgageClickGa4(mc('Veteran', 'buy'));
        window.open(
            'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.383026519;dc_trk_aid=573901919;dc_trk_cid=206110318;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
            '_blank'
        );
        $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
        return;
    } else if (sendOpt.triggerId === 'veteranseligibility1NewDpp') {
        emitMortgageClickGa4(mc('Veteran', 'buy'));
        window.open('https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.402829196;dc_trk_aid=594717722;dc_trk_cid=221874282;dc_lat=;dc_rdid=;', '_blank');
        $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
        return;
    } else if (sendOpt.triggerId === 'veteranseligibility2') {
        // url = `https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.359548222;dc_trk_aid=550413068;dc_trk_cid=186553192;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=`
        emitMortgageClickGa4(mc('Veteran', 'buy'));
        window.open(
            'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.392850339;dc_trk_aid=584162619;dc_trk_cid=213404959;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
            '_blank'
        );
        $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
        return;
    } else if (sendOpt.triggerId === 'getquotes') {
        if (url === 'insurance1') {
            loadInsurancePop(sendOpt.zipcode, 'getquotes_dpp');
            return;
        } else if (url === 'insurance2') {
            loadInsurancePop(sendOpt.zipcode, 'getaffordableinsurance_thankyoupage');
            return;
        }
    } else {
        if (url) {
            const vu =
                (sendOpt.triggerId && String(sendOpt.triggerId).toLowerCase().includes('veteran')) ||
                (sendOpt.url && String(sendOpt.url).toLowerCase().includes('veteran')) ||
                (String(url).toLowerCase().includes('veteran'));
            emitMortgageClickGa4({
                label: vu ? 'Veteran' : 'Bankrate',
                linkName: linkName(),
                category: mortgageListingCategory(sendOpt),
            });
            $eventBus.$emit('ga', { ec: 'partner', en: 'partner_lead_created', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
        }
    }

    //only overwrite default case for thoes cases where we need to open a new tab
    if (['apply', 'getpreapproved', 'findalender'].includes(sendOpt.triggerId)) {
        loadDefaultMRCPopUp(sendOpt.triggerId, sendOpt.cta, sendOpt);
        return;
    }

    if (url === 'betterfinance' || url === 'betterpreapproved') {
        loadMortgagePopUp(sendOpt.triggerId, sendOpt.pid, sendOpt.cta, mc('Bankrate', 'buy'));
        return;
    } else if (url === 'refinance') {
        sendOpt.pid = 'myfi_movoto';
        loadMortgagePopUp(sendOpt.triggerId, sendOpt.pid, sendOpt.cta, mc('Bankrate', 'refi'));
        return;
    } else if (
        url == 'preapprove' &&
        sendOpt.isAppendUtmCta &&
        (sendOpt.triggerId == 'calculatepayment' || sendOpt.triggerId == 'getpreapproved' || sendOpt.triggerId == 'prequalify' || sendOpt.triggerId == 'findalender')
    ) {
        sendOpt.pid = 'myfi_movoto';
        loadMortgagePopUp(sendOpt.triggerId, sendOpt.pid, sendOpt.cta, mc('Bankrate', 'buy'));
        return;
    } else if (['preapproveCalc', 'preapprove'].includes(url)) {
        if (['MOBILE', 'LAND'].includes(sendOpt.propertyType)) {
            loadMortgagePopUp(sendOpt.triggerId, sendOpt.pid, sendOpt.cta, mc('Bankrate', 'buy'));
        }
        return;
    } else if (url === 'prequalifylandmobile') {
        loadMortgagePopUp(sendOpt.triggerId, sendOpt.pid, sendOpt.cta, mc('Bankrate', 'buy'));
        return;
    } else if (url === 'myfinance') {
        loadMortgagePopUp(sendOpt.triggerId, sendOpt.pid, sendOpt.cta, mc('Bankrate', 'buy'));
        return;
    } else if (url === 'rentalCredit') {
        var dict1 = {
            checkrentalCredit: 'rentaldppatf',
        };
        var subId1 = dict1[sendOpt.triggerId] || sendOpt.triggerId;
        // emitMortgageClickGa4({
        //     label: 'Cordless',
        //     linkName: linkName(subId1),
        //     category: mortgageListingCategory(sendOpt),
        // });
        window.open(
            `https://www.gofreecredit.com/tu/r/f65e0b6641b23029484e89ee258fd7e8?pid=${sendOpt.pid}&fname=${sendOpt.firstName}&lname=${sendOpt.lastName}&zip=${sendOpt.zipcode}&email=${sendOpt.email}&phone1=${sendOpt.phone}&address1=&city=${sendOpt.city}&state=${sendOpt.state}&subid=${subId1}`,
            '_blank'
        );
        return;
    } else if (sendOpt.url === 'veteransUnited') {
        var params = {};
        if (sendOpt.triggerId === 'veteranseligibility') {
            params = { nid: '4411758', bid: '328819326', aid: '521119045', cid: '166903281' };
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open(
                `https://ad.doubleclick.net/ddm/trackclk/N7384.${params.nid}MOVOTO_AFFORDABILIT/B27281448.${params.bid};dc_trk_aid=${params.aid};dc_trk_cid=${params.cid};dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=`,
                '_blank'
            );
            return;
        } else if (sendOpt.triggerId === 'veteranscanafford') {
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open(
                'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.392850873;dc_trk_aid=584163027;dc_trk_cid=214030662;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
                '_blank'
            );
            $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
            return;
        } else if (sendOpt.triggerId === 'veteranscanafford1') {
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open(
                'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.333845379;dc_trk_aid=525666632;dc_trk_cid=169972431;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=',
                '_blank'
            );
            return;
        } else if (sendOpt.triggerId === 'veteranscanafford2') {
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open(
                'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.334076439;dc_trk_aid=525934527;dc_trk_cid=169752983;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=',
                '_blank'
            );
            return;
        } else if (sendOpt.triggerId === 'veteranscanafford3') {
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open(
                'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.334077147;dc_trk_aid=526252873;dc_trk_cid=169755869;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=',
                '_blank'
            );
            $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
            return;
        } else if (sendOpt.triggerId === 'veteranscanafford6') {
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open(
                'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.388062853;dc_trk_aid=579360909;dc_trk_cid=210563133;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=;dc_tdv=1',
                '_blank'
            );
            $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
            return;
        } else if (sendOpt.triggerId === 'veteranscanafford6NewDpp') {
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open('https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.403100178;dc_trk_aid=594676090;dc_trk_cid=221277862;dc_lat=;dc_rdid=;', '_blank');
            $eventBus.$emit('ga', { ec: 'partner_mortgage_listings_session', en: 'lender_site', el: elDict[sendOpt.triggerId] || elDict[url] || url, props: props });
            return;
        }
    } else if (url === 'veteransUnited2') {
        if (sendOpt.triggerId === 'veteranseligibility3') {
            emitMortgageClickGa4(mc('Veteran', 'buy'));
            window.open(
                'https://ad.doubleclick.net/ddm/trackclk/N7384.146215MOVOTO/B27281448.335741447;dc_trk_aid=527838432;dc_trk_cid=171090347;dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=;ltd=',
                '_blank'
            );
            return;
        }
    }

    if (sendOpt.utm_email) {
        url += `&utm_email=${sendOpt.utm_email}`;
    }
    if (sendOpt.utm_notification) {
        url += `&utm_notification=${sendOpt.utm_notification}`;
    }

    if (sendOpt.pageType && url && !url.includes('www.veteransunited.com')) {
        url += `&pageType=${sendOpt.pageType}`;
    }

    if (context.user && context.user.id) {
        url += `&ojo-uid=${context.user.id}`;
    }
    const contextObj = {
        propertyType: sendOpt.propertyType,
        propertyValue: sendOpt.price || '',
        propertyCity: sendOpt.city || '',
        propertyState: sendOpt.state || '',
        zipcode: sendOpt.zipcode || sendOpt.zip || '',
        purchasePrice: sendOpt.price || '',
        email: sendOpt.email || '',
        phone: sendOpt.phone || '',
        firstName: sendOpt.firstName || '',
        lastName: sendOpt.lastName || '',
    };
    const postData = {
        name: category ? 'SHOW_EMBEDDED_WIDGET' : 'SHOW_EMBEDDED_FORM',
        triggerId: `${sendOpt.triggerId}${sendOpt.isAppendUtmCta ? 'SF' : ''}`,
        category: category,
        formUrl: category ? undefined : url,
        context: contextObj,
    };

    loadBankratePop().then(function () {
        console.log(postData);
        emitMortgageClickGa4(mc('Bankrate'));
        window.postMessage(postData, '*');
    });
};
